import { useEffect, useState, useCallback } from 'react';
import { studentMatches } from '../lib/search';
import { MobileHeader, HeaderAction, MobileToolbar, Chip, BanknotePlus, useShell, fmtAmount } from '../components/MobileHeader';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronUp, ChevronDown, Plus, Search, MessageSquare, Copy, Share2, Pencil, Check, SlidersHorizontal, Receipt, X } from 'lucide-react';
import { useLang } from '../i18n';
import { api } from '../api';
import { Avatar } from './Dashboard';
import PaymentModal from '../components/PaymentModal';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';

const getLastMethodFin = () => { try { return localStorage.getItem('bachata_last_method') || 'cash'; } catch { return 'cash'; } };
const METHOD_LABELS = { cash: 'Cash', twint: 'Twint', virement: 'Vir.' };

const DEFAULT_REMINDER_TEMPLATE = `Bonjour {prenom} ! 👋

Petit rappel concernant ton solde de cours de danse.

📊 Solde actuel : {solde} CHF

📅 Séances à régulariser :
{listeCoursDevus}

Merci de me contacter pour régulariser ! 🙏

{date}`;

function computeUnpaidSessions(atts, balance_chf) {
  if (!balance_chf || balance_chf >= 0) return 'Aucun impayé en cours.';
  const owed = Math.abs(balance_chf);
  const presentAtts = [...atts]
    .filter(a => a.present && (a.effective_amount || 0) > 0)
    .sort((a, b) => b.class_date.localeCompare(a.class_date));

  let accumulated = 0;
  const uncovered = [];
  for (const att of presentAtts) {
    if (accumulated >= owed) break;
    accumulated += att.effective_amount;
    uncovered.push(att);
  }

  // Grouper par cours, conserver les dates triées
  const grouped = {};
  for (const att of uncovered) {
    const key = att.class_title || 'Cours';
    if (!grouped[key]) grouped[key] = { title: key, sessions: [] };
    grouped[key].sessions.push({ date: att.class_date, amount: att.effective_amount });
  }

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('fr-CH', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const lines = Object.values(grouped).map(g => {
    const total = g.sessions.reduce((s, x) => s + x.amount, 0);
    const dateLines = g.sessions
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(s => `  - ${formatDate(s.date)} — ${s.amount.toFixed(0)} CHF`)
      .join('\n');
    return `${g.title} (${g.sessions.length} séance${g.sessions.length > 1 ? 's' : ''} · total ${total.toFixed(0)} CHF) :\n${dateLines}`;
  });

  return lines.length > 0 ? lines.join('\n\n') : `Montant dû : ${owed.toFixed(0)} CHF`;
}

export default function Finance() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [received, setReceived] = useState(null);  // v2.14 : total reçu (vert)
  const [showRecent, setShowRecent] = useState(false);  // v2.16 : derniers paiements
  const [students, setStudents] = useState([]);
  const [groupCourses, setGroupCourses] = useState([]);
  const [filter, setFilter] = useState('all');
  const [filterCourse, setFilterCourse] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('alpha');
  const [sortDir, setSortDir] = useState('asc');

  // ── Message rappel ──
  const [selectedForMsg, setSelectedForMsg] = useState(null);
  const [msgAtts, setMsgAtts] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgExpanded, setMsgExpanded] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [template, setTemplate] = useState(() => {
    try { return localStorage.getItem('payment_reminder_template') || DEFAULT_REMINDER_TEMPLATE; }
    catch { return DEFAULT_REMINDER_TEMPLATE; }
  });
  const [draftTemplate, setDraftTemplate] = useState(template);

  const handleSelectForMsg = useCallback(async (d) => {
    setSelectedForMsg(d);
    setMsgExpanded(true);
    setMsgLoading(true);
    try {
      const atts = await api.get(`/students/${d.student.id}/attendances-detail`);
      setMsgAtts(atts || []);
    } catch { setMsgAtts([]); }
    finally { setMsgLoading(false); }
  }, []);

  const generateMessageForStudent = useCallback((d, atts) => {
    const { student, balance_chf } = d;
    const listeCoursDevus = computeUnpaidSessions(atts, balance_chf);
    const signe = (balance_chf || 0) > 0 ? '+' : '';
    return template
      .replace(/{prenom}/g, student.first_name)
      .replace(/{nom}/g, student.last_name)
      .replace(/{solde}/g, `${signe}${(balance_chf || 0).toFixed(0)} CHF`)
      .replace(/{listeCoursDevus}/g, listeCoursDevus)
      .replace(/{date}/g, new Date().toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' }));
  }, [template]);

  const generateMessage = () => selectedForMsg ? generateMessageForStudent(selectedForMsg, msgAtts) : '';

  const [quickLoadingIds, setQuickLoadingIds] = useState(new Set());

  const handleQuickAction = useCallback(async (d, action) => {
    const key = d.student.id + '_' + action;
    setQuickLoadingIds(prev => new Set([...prev, key]));
    try {
      const atts = await api.get(`/students/${d.student.id}/attendances-detail`);
      const msg = generateMessageForStudent(d, atts);
      if (action === 'copy') {
        await navigator.clipboard.writeText(msg);
        toast.success('Message copié !');
      } else {
        if (navigator.share) {
          await navigator.share({ text: msg });
        } else {
          await navigator.clipboard.writeText(msg);
          toast.success('Message copié (partage non disponible sur ce navigateur)');
        }
      }
    } catch (e) {
      if (action === 'copy') toast.error('Erreur de copie');
    } finally {
      setQuickLoadingIds(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  }, [generateMessageForStudent]);

  const handleCopyMessage = () => {
    const msg = generateMessage();
    navigator.clipboard.writeText(msg).then(() => toast.success('Message copié !')).catch(() => toast.error('Erreur de copie'));
  };

  const handleShareMessage = async () => {
    const msg = generateMessage();
    if (navigator.share) {
      try { await navigator.share({ text: msg }); }
      catch { /* user cancelled */ }
    } else {
      navigator.clipboard.writeText(msg).then(() => toast.success('Message copié (partage non disponible sur ce navigateur)'));
    }
  };

  const handleSaveTemplate = () => {
    setTemplate(draftTemplate);
    localStorage.setItem('payment_reminder_template', draftTemplate);
    setEditingTemplate(false);
    toast.success('Modèle sauvegardé');
  };

  const handleHeaderSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };
  const [showObsolete, setShowObsolete] = useState(false);
  const [showFilters, setShowFilters] = useState(false);  // mobile : panneau ⚙ (rôle, cours)
  const [loading, setLoading] = useState(true);
  const [payStudent, setPayStudent] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const [quickPayingId, setQuickPayingId] = useState(null);

  const handleQuickPay = async (d) => {
    if (quickPayingId) return;
    const amount = d.total_remaining > 0 ? d.total_remaining : 20;
    const method = getLastMethodFin();
    const pkg = (d.packages || []).find(p => Math.max(0, p.total_price - (p.paid_amount || 0)) > 0);
    setQuickPayingId(d.student.id);
    try {
      await api.post('/payments', {
        student_id: d.student.id,
        amount: parseFloat(amount),
        date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
        method,
        notes: '',
        package_id: pkg?.id || null,
        group_course_id: null,
      });
      toast.success(`✓ ${amount} CHF · ${d.student.first_name} ${d.student.last_name}`);
      fetchData();
    } catch {
      toast.error('Erreur paiement');
    } finally { setQuickPayingId(null); }
  };

  const fetchData = async () => {
    const [res, stds, gc] = await Promise.all([
      api.get('/finance').catch(() => []),
      api.get('/students').catch(() => []),
      api.get('/group-courses').catch(() => []),
    ]);
    setData(res);
    setStudents(stds);
    setGroupCourses(gc);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { api.get('/finance/received').then(r => setReceived(r.total)).catch(() => setReceived(null)); }, []);

  // Argent encaissé mais pas encore attribué à un élève (caisse « attribuer plus tard »)
  const shell = useShell();
  const [unassigned, setUnassigned] = useState([]);
  useEffect(() => {
    api.get('/cashbox/pending').then(l => setUnassigned(l || [])).catch(() => setUnassigned([]));
  }, [shell.pendingCount]);
  const unassignedTotal = unassigned.reduce((sum, p) => sum + (p.amount || 0), 0);
  useEffect(() => { setPage(1); }, [search, filter, filterCourse, filterRole, sortBy]);

  const totalOwed = data.reduce((s, d) => s + (d.total_remaining > 0 ? d.total_remaining : 0), 0);
  const debtorsCount = data.filter(d => d.total_remaining > 0).length;

  const activeGroupCourses = groupCourses.filter(gc => showObsolete || gc.is_active !== false);

  const filtered = data
    .filter(d => {
      if (search.trim()) {
        return studentMatches(d.student, search);
      }
      return true;
    })
    .filter(d => {
      if (filter === 'owes') return d.total_remaining > 0;
      return true;
    })
    .filter(d => {
      if (filterRole !== 'all') {
        const s = students.find(s => s.id === d.student.id);
        if (!s || s.role !== filterRole) return false;
      }
      if (filterCourse === 'all') return true;
      if (filterCourse === 'private') return d.packages?.some(p => p.type === 'private');
      const gc = groupCourses.find(c => c.id === filterCourse);
      return gc?.enrolled_students?.some(e => e.student_id === d.student.id) ?? false;
    })
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortBy === 'balance') {
        return ((a.balance_chf ?? 0) - (b.balance_chf ?? 0)) * dir;
      }
      const na = `${a.student.last_name} ${a.student.first_name}`.toLowerCase();
      const nb = `${b.student.last_name} ${b.student.first_name}`.toLowerCase();
      return na.localeCompare(nb, 'fr') * dir;
    });

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-4 border-[#0288D1]/30 border-t-[#0288D1] rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto" data-testid="finance-page">
      <MobileHeader title={t('finance.title')}
        search={{ value: search, onChange: setSearch, placeholder: 'Nom, Instagram, téléphone…', scope: 'students' }}
        actions={<>
          <HeaderAction icon={Receipt} label="Derniers paiements" active={showRecent} testId="recent-payments-btn"
            onClick={() => setShowRecent(v => !v)} />
          <HeaderAction icon={SlidersHorizontal} label="Filtres" active={showFilters} testId="finance-filters-btn"
            badge={(filterRole !== 'all') + (filterCourse !== 'all') || null} onClick={() => setShowFilters(v => !v)} />
        </>} />
      {showRecent && <RecentPayments onClose={() => setShowRecent(false)} onOpen={(sid) => navigate(`/students/${sid}`)} />}
      {/* Mobile : le filtre propre à Finance (Tous / Dettes) reste visible ; le reste est dans ⚙ */}
      <MobileToolbar>
        {[['all', 'Tous'], ['owes', 'Dettes']].map(([val, label]) => (
          <Chip key={val} active={filter === val} onClick={() => setFilter(val)} testId={`finance-chip-${val}`}>{label}</Chip>
        ))}
      </MobileToolbar>
      {showFilters && (
        <div className="mobile-only flex flex-col gap-3 mb-4 p-3 bg-white rounded-2xl border border-slate-100" data-testid="finance-filters-panel">
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-full text-xs font-bold uppercase tracking-wide text-slate-400">Rôle</span>
            {[['all', 'F + L', '#334155'], ['follower', 'Followers', '#ec4899'], ['leader', 'Leaders', '#0288D1']].map(([val, label, color]) => (
              <Chip key={val} active={filterRole === val} onClick={() => setFilterRole(val)} color={color}>{label}</Chip>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-full text-xs font-bold uppercase tracking-wide text-slate-400">Cours</span>
            <Chip active={filterCourse === 'all'} onClick={() => setFilterCourse('all')} color="#334155">Tous</Chip>
            {activeGroupCourses.map(gc => (
              <Chip key={gc.id} active={filterCourse === gc.id} color={gc.color || '#0288D1'}
                onClick={() => setFilterCourse(gc.id === filterCourse ? 'all' : gc.id)}>{gc.name}</Chip>
            ))}
            <Chip active={filterCourse === 'private'} color="#26A69A" onClick={() => setFilterCourse(filterCourse === 'private' ? 'all' : 'private')}>Privé</Chip>
            {groupCourses.some(gc => gc.is_active === false) && (
              <Chip active={showObsolete} color="#94a3b8" onClick={() => setShowObsolete(v => !v)}>Obsolètes</Chip>
            )}
          </div>
        </div>
      )}

      {unassigned.length > 0 && (
        <button onClick={() => shell.openCashbox?.(null, true)} data-testid="finance-unassigned"
          className="w-full mb-4 flex items-center gap-3 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200 text-left hover:bg-amber-100/60 transition-colors">
          <span className="w-9 h-9 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0"><BanknotePlus size={18} /></span>
          <span className="flex-1 min-w-0">
            <span className="block text-sm font-bold text-amber-800">Encaissé, non attribué : {fmtAmount(unassignedTotal)} CHF ({unassigned.length})</span>
            <span className="block text-xs text-amber-700">Pas encore compté dans les soldes des élèves — toucher pour attribuer</span>
          </span>
          <ChevronRight size={18} className="text-amber-500 shrink-0" />
        </button>
      )}

      {/* Search bar avec chips F / L intégrés (PC) */}
      <div className="mobile-hidden relative mb-4">
        <div className="flex gap-1 absolute left-3 top-1/2 -translate-y-1/2 z-10">
          {[['all', 'Tous'], ['follower', 'F'], ['leader', 'L']].map(([val, label]) => (
            <button key={val} onClick={() => setFilterRole(val)}
              className={cn('px-2 py-0.5 rounded-md text-[11px] font-bold border transition-colors',
                filterRole === val
                  ? val === 'follower' ? 'bg-pink-500 text-white border-pink-500'
                    : val === 'leader' ? 'bg-[#0288D1] text-white border-[#0288D1]'
                    : 'bg-slate-700 text-white border-slate-700'
                  : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
              )}
              data-testid={`finance-role-${val}`}>
              {label}
            </button>
          ))}
        </div>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher par nom, Instagram, téléphone…"
          className="w-full h-11 pl-28 pr-10 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0288D1]/20 focus:border-[#0288D1] shadow-sm"
          data-testid="finance-search-input"
        />
        {search ? (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">×</button>
        ) : (
          <Search size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        )}
      </div>

      {/* Filter row — statut + tri (PC) */}
      <div className="mobile-hidden flex flex-wrap items-center gap-2 mb-2">
        {[['all', 'Tous'], ['owes', 'Dettes']].map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)}
            className={cn('px-4 py-2 rounded-xl text-sm font-semibold transition-colors',
              filter === val ? 'bg-[#0288D1] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50')}
            data-testid={`finance-filter-${val}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Filter row 2 — cours (PC ; sur mobile → panneau ⚙) */}
      <div className="mobile-hidden flex flex-wrap gap-2 mb-4">
        {activeGroupCourses.map(gc => (
          <button key={gc.id}
            onClick={() => setFilterCourse(gc.id === filterCourse ? 'all' : gc.id)}
            className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors',
              filterCourse === gc.id ? 'text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50')}
            style={filterCourse === gc.id ? { backgroundColor: gc.color || '#0288D1' } : {}}
            data-testid={`filter-course-${gc.id}`}>
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: filterCourse === gc.id ? 'white' : (gc.color || '#0288D1') }} />
            {gc.name}
          </button>
        ))}
        <button
          onClick={() => setFilterCourse(filterCourse === 'private' ? 'all' : 'private')}
          className={cn('px-3 py-1.5 rounded-lg text-xs font-bold transition-colors',
            filterCourse === 'private' ? 'bg-[#26A69A] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50')}
          data-testid="filter-course-private">
          Privé
        </button>
        {groupCourses.some(gc => gc.is_active === false) && (
          <button onClick={() => setShowObsolete(v => !v)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-400 border border-dashed border-slate-300 hover:bg-slate-50 transition-colors"
            data-testid="toggle-obsolete-courses">
            {showObsolete ? '↑ Masquer obsolètes' : '+ Obsolètes'}
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-center">
          <div className="text-2xl font-extrabold text-red-500 font-manrope">{totalOwed.toFixed(0)}</div>
          <div className="text-xs text-slate-500 mt-0.5">CHF dûs</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-center" data-testid="total-received">
          <div className="text-2xl font-extrabold text-[#26A69A] font-manrope">{received === null ? '…' : received.toFixed(0)}</div>
          <div className="text-xs text-slate-500 mt-0.5">CHF reçus au total</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-center">
          <div className="text-2xl font-extrabold text-slate-900 font-manrope">{debtorsCount}</div>
          <div className="text-xs text-slate-500 mt-0.5">{t('finance.owesOnly')}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm text-center">
          <div className="text-2xl font-extrabold text-[#26A69A] font-manrope">{data.length - debtorsCount}</div>
          <div className="text-xs text-slate-500 mt-0.5">{t('finance.fullyPaid')}</div>
        </div>
      </div>

      {/* Stats */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400 text-sm">{t('finance.noData')}</div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="mobile-hidden bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  {[
                    { key: 'alpha', label: t('finance.student') },
                    { key: 'balance', label: 'Solde restant' },
                    { key: null, label: 'Restants' },
                    { key: null, label: t('finance.progress') },
                  ].map(({ key, label }) => (
                    <th key={label}
                      onClick={key ? () => handleHeaderSort(key) : undefined}
                      className={cn('text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider select-none',
                        key && 'cursor-pointer hover:text-[#0288D1] transition-colors')}>
                      <span className="flex items-center gap-1">
                        {label}
                        {key && sortBy === key && <span className="text-[#0288D1]">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                        {key && sortBy !== key && <span className="text-slate-300">↕</span>}
                      </span>
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE).map((d, i) => (
                  <FinanceRow key={d.student.id} d={d} i={i} onClick={() => navigate(`/students/${d.student.id}`)} onPay={() => setPayStudent(d)} onSelectMsg={() => handleSelectForMsg(d)} onQuickCopy={() => handleQuickAction(d, 'copy')} onQuickShare={() => handleQuickAction(d, 'share')} quickLoadingIds={quickLoadingIds} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="mobile-only grid gap-3 xl:grid-cols-2 items-start">
            {filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE).map(d => (
              <MobileFinanceCard key={d.student.id} d={d} onClick={() => navigate(`/students/${d.student.id}`)} onPay={() => setPayStudent(d)} onQuickPay={() => handleQuickPay(d)} isQuickPaying={quickPayingId === d.student.id} />
            ))}
          </div>

          {/* Pagination */}
          {Math.ceil(filtered.length / PAGE_SIZE) > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-slate-100">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1.5 rounded-lg text-xs font-bold border border-slate-200 bg-white disabled:opacity-40 hover:bg-slate-50 transition-colors">
                ‹ Préc.
              </button>
              <span className="text-xs text-slate-500 font-medium">{page} / {Math.ceil(filtered.length / PAGE_SIZE)}</span>
              <button onClick={() => setPage(p => Math.min(Math.ceil(filtered.length / PAGE_SIZE), p + 1))} disabled={page === Math.ceil(filtered.length / PAGE_SIZE)}
                className="px-3 py-1.5 rounded-lg text-xs font-bold border border-slate-200 bg-white disabled:opacity-40 hover:bg-slate-50 transition-colors">
                Suiv. ›
              </button>
            </div>
          )}
        </>
      )}

      {payStudent && (
        <PaymentModal
          studentId={payStudent.student.id}
          studentName={`${payStudent.student.first_name} ${payStudent.student.last_name}`}
          packages={payStudent.packages || []}
          onClose={() => setPayStudent(null)}
          onSaved={() => { setPayStudent(null); fetchData(); }}
        />
      )}

      {/* ── Encart message rappel de paiement ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mt-6" data-testid="payment-reminder-panel">
        <button onClick={() => setMsgExpanded(v => !v)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors rounded-2xl">
          <div className="flex items-center gap-2">
            <MessageSquare size={16} className="text-[#0288D1]" />
            <span className="font-semibold text-slate-700">Message de rappel de paiement</span>
            {selectedForMsg && (
              <span className="text-xs font-bold text-white bg-[#0288D1] px-2 py-0.5 rounded-full ml-1">
                {selectedForMsg.student.first_name} {selectedForMsg.student.last_name}
              </span>
            )}
          </div>
          {msgExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </button>

        {msgExpanded && (
          <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-4">
            {/* Instructions variables */}
            <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-3 py-2">
              Variables : <code className="text-[#0288D1]">{'{prenom}'}</code>, <code className="text-[#0288D1]">{'{nom}'}</code>, <code className="text-[#0288D1]">{'{solde}'}</code>, <code className="text-[#0288D1]">{'{listeCoursDevus}'}</code>, <code className="text-[#0288D1]">{'{date}'}</code>
              {' · '}Cliquer une ligne du tableau pour sélectionner un élève.
            </div>

            {/* Template editor */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Modèle de message</span>
                {editingTemplate ? (
                  <div className="flex gap-1.5">
                    <button onClick={handleSaveTemplate} className="flex items-center gap-1 text-xs font-bold text-[#26A69A] bg-[#26A69A]/10 px-2 py-1 rounded-lg hover:bg-[#26A69A]/20">
                      <Check size={11} /> Enregistrer
                    </button>
                    <button onClick={() => { setEditingTemplate(false); setDraftTemplate(template); }}
                      className="text-xs font-bold text-slate-400 px-2 py-1 rounded-lg hover:bg-slate-100">
                      Annuler
                    </button>
                  </div>
                ) : (
                  <button onClick={() => { setDraftTemplate(template); setEditingTemplate(true); }}
                    className="flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-slate-600 px-2 py-1 rounded-lg hover:bg-slate-100">
                    <Pencil size={11} /> Modifier
                  </button>
                )}
              </div>
              {editingTemplate ? (
                <textarea
                  value={draftTemplate}
                  onChange={e => setDraftTemplate(e.target.value)}
                  rows={10}
                  className="w-full text-xs font-mono border border-[#0288D1]/30 rounded-xl px-3 py-2 focus:outline-none focus:border-[#0288D1] text-slate-700 bg-[#0288D1]/3 resize-y"
                  data-testid="template-editor"
                />
              ) : (
                <pre className="text-xs text-slate-500 bg-slate-50 rounded-xl px-3 py-2 whitespace-pre-wrap font-sans">{template}</pre>
              )}
            </div>

            {/* Preview */}
            {selectedForMsg && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Aperçu · {selectedForMsg.student.first_name} {selectedForMsg.student.last_name}
                  </span>
                  {msgLoading && <span className="text-[10px] text-slate-400 animate-pulse">Chargement…</span>}
                </div>
                <div className="bg-[#E8F4F8] rounded-2xl px-4 py-3 text-sm text-slate-700 whitespace-pre-wrap border border-[#0288D1]/15" data-testid="message-preview">
                  {msgLoading ? 'Calcul des séances non couvertes…' : generateMessage()}
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={handleCopyMessage}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0288D1] text-white text-sm font-bold hover:bg-[#0277BD] transition-colors"
                    data-testid="copy-message-btn">
                    <Copy size={13} /> Copier
                  </button>
                  <button onClick={handleShareMessage}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-700 text-white text-sm font-bold hover:bg-slate-800 transition-colors"
                    data-testid="share-message-btn">
                    <Share2 size={13} /> Partager
                  </button>
                </div>
              </div>
            )}

            {!selectedForMsg && (
              <div className="text-center py-4 text-sm text-slate-400 italic">
                Sélectionnez un élève dans le tableau ci-dessus pour générer son message.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FinanceRow({ d, i, onClick, onPay, onSelectMsg, onQuickCopy, onQuickShare, quickLoadingIds }) {
  const { t } = useLang();
  const isEven = i % 2 === 0;
  const isCopyLoading = quickLoadingIds?.has(d.student.id + '_copy');
  const isShareLoading = quickLoadingIds?.has(d.student.id + '_share');

  // Combined remaining: "2 CC / 10h CP"
  const remainingParts = [];
  if (d.classes_remaining > 0) remainingParts.push(`${d.classes_remaining} CC`);
  if (d.hours_remaining > 0) remainingParts.push(`${d.hours_remaining}h CP`);
  const remainingLabel = remainingParts.join(' / ') || '—';

  return (
    <tr
      className={cn('cursor-pointer hover:bg-slate-50 transition-colors', isEven ? 'bg-white' : 'bg-slate-50/50')}
      onClick={onClick}
      data-testid="finance-row"
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Avatar name={`${d.student.first_name} ${d.student.last_name}`} size="sm" />
          <div>
            <div className="font-semibold text-slate-800">{d.student.first_name} {d.student.last_name}</div>
            {d.student.role && (
              <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded-full',
                d.student.role === 'leader' ? 'bg-blue-100 text-blue-600' : 'bg-pink-100 text-pink-600')}>
                {d.student.role === 'leader' ? 'Leader' : 'Follower'}
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {(() => {
          const solde = d.balance_chf ?? 0;
          return (
            <span className={cn('font-bold tabular-nums', solde < 0 ? 'text-red-500' : 'text-[#26A69A]')}>
              {solde > 0 ? '+' : ''}{solde.toFixed(0)} CHF
            </span>
          );
        })()}
      </td>
      <td className="px-4 py-3 text-slate-600 text-xs font-semibold">{remainingLabel}</td>
      <td className="px-4 py-3 min-w-[120px]">
        <ProgressBar pct={d.progress_pct} />
      </td>
      <td className="px-4 py-3 flex items-center gap-1" onClick={e => e.stopPropagation()}>
        <button
          onClick={onPay}
          className="flex items-center gap-1 text-xs font-bold text-[#0288D1] bg-[#0288D1]/10 px-2 py-1 rounded-lg hover:bg-[#0288D1]/20 transition-colors"
          data-testid="quick-pay-btn">
          <Plus size={12} /> {t('finance.addPayment')}
        </button>
        <button
          onClick={onSelectMsg}
          title="Voir le message"
          className="w-6 h-6 flex items-center justify-center rounded-lg text-slate-300 hover:bg-[#0288D1]/10 hover:text-[#0288D1] transition-colors"
          data-testid="row-message-btn">
          <MessageSquare size={12} />
        </button>
        <button
          onClick={onQuickCopy}
          title="Copier le message"
          disabled={isCopyLoading}
          className="w-6 h-6 flex items-center justify-center rounded-lg text-slate-300 hover:bg-emerald-50 hover:text-emerald-500 transition-colors disabled:opacity-50"
          data-testid="row-copy-btn">
          {isCopyLoading ? <span className="w-2.5 h-2.5 border border-slate-400 border-t-transparent rounded-full animate-spin" /> : <Copy size={12} />}
        </button>
        <button
          onClick={onQuickShare}
          title="Partager le message"
          disabled={isShareLoading}
          className="w-6 h-6 flex items-center justify-center rounded-lg text-slate-300 hover:bg-slate-100 hover:text-slate-600 transition-colors disabled:opacity-50"
          data-testid="row-share-btn">
          {isShareLoading ? <span className="w-2.5 h-2.5 border border-slate-400 border-t-transparent rounded-full animate-spin" /> : <Share2 size={12} />}
        </button>
      </td>
    </tr>
  );
}

function MobileFinanceCard({ d, onClick, onPay, onQuickPay, isQuickPaying }) {
  const { t } = useLang();
  const lastMethod = getLastMethodFin();
  const hasPrivateBalance = (d.private_balance || 0) > 0;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="finance-card">
      <div className="p-4 cursor-pointer" onClick={onClick}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Avatar name={`${d.student.first_name} ${d.student.last_name}`} />
            <div>
              <div className="font-bold text-slate-900">{d.student.first_name} {d.student.last_name}</div>
              <div className="text-xs text-slate-400">
                {d.packages?.[0]?.type === 'group'
                  ? `${d.packages[0].total_classes} cours · ${d.classes_remaining} restants`
                  : d.packages?.[0]?.type === 'mixed'
                    ? `${d.packages[0].total_classes} collectif · ${d.packages[0].total_hours}h privé · ${d.classes_remaining} cours restants`
                    : d.packages?.[0]?.type === 'private'
                      ? `${d.packages[0].total_hours}h · ${d.hours_remaining}h restantes`
                      : hasPrivateBalance
                        ? `${d.private_sessions_owed} CHF séances privées`
                        : 'Aucun forfait'}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="font-bold text-slate-800">{d.total_price} CHF</div>
            <div className={cn('text-sm font-bold tabular-nums', (d.balance_chf ?? 0) < 0 ? 'text-red-500' : 'text-[#26A69A]')}>
              {(d.balance_chf ?? 0) >= 0 ? `+${Math.abs(d.balance_chf ?? 0).toFixed(0)}` : `${(d.balance_chf ?? 0).toFixed(0)}`} CHF
            </div>
          </div>
        </div>
        <ProgressBar pct={d.progress_pct} />
        <div className="text-xs text-slate-400 mt-1">{d.paid_amount} CHF payés · {d.progress_pct}%</div>
        {d.group_balance > 0 && <div className="text-xs text-blue-500 mt-0.5">{d.group_balance} CHF cours collectifs</div>}
        {hasPrivateBalance && <div className="text-xs text-orange-500 mt-0.5">{d.private_balance} CHF privé sans forfait</div>}
      </div>
      {d.total_remaining > 0 && (
        <div className="border-t border-slate-100 px-4 py-2 flex items-center gap-2">
          <button
            onClick={onQuickPay}
            disabled={isQuickPaying}
            className="flex-1 flex items-center justify-center gap-1.5 text-sm font-bold text-white bg-[#26A69A] h-9 rounded-xl active:scale-95 transition-all disabled:opacity-60"
            data-testid="mobile-quick-pay-btn"
          >
            {isQuickPaying ? '…' : `✓ ${d.total_remaining} CHF · ${METHOD_LABELS[lastMethod] || 'Cash'}`}
          </button>
          <button
            onClick={onPay}
            className="w-9 h-9 rounded-xl border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50 transition-colors shrink-0"
            data-testid="mobile-pay-options-btn"
            title="Options de paiement"
          >
            <Plus size={14} className="text-slate-500" />
          </button>
        </div>
      )}
    </div>
  );
}

function ProgressBar({ pct }) {
  const color = pct === 100 ? 'bg-[#26A69A]' : pct > 50 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="w-full bg-slate-100 rounded-full h-2">
      <div className={cn('h-2 rounded-full transition-all', color)} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

/** v2.16 — les derniers paiements reçus (les plus récents d'abord) ; toucher une ligne ouvre la fiche de l'élève. */
function RecentPayments({ onClose, onOpen }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get('/finance/recent-payments?limit=50').then(setRows).catch(() => setRows([])); }, []);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 mb-4" data-testid="recent-payments">
      <div className="flex items-center mb-2">
        <span className="font-bold text-slate-900 text-sm flex items-center gap-1.5"><Receipt size={15} className="text-[#26A69A]" /> Derniers paiements</span>
        <button type="button" onClick={onClose} className="ml-auto text-slate-400" aria-label="Fermer"><X size={16} /></button>
      </div>
      {rows === null ? <p className="text-sm text-slate-400 px-1">…</p> : rows.length === 0 ? <p className="text-sm text-slate-400 px-1">Aucun paiement.</p> : (
        <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
          {rows.map(r => (
            <button key={r.id || `${r.date}-${r.student_id}-${r.amount}`} type="button" onClick={() => onOpen(r.student_id)}
              className="w-full flex items-center gap-3 py-2 px-1 text-left hover:bg-slate-50 rounded-lg" data-testid="recent-payment-row">
              <span className="text-xs text-slate-400 w-20 shrink-0">{r.date ? format(new Date(r.date), 'dd.MM.yyyy') : '—'}</span>
              <span className="flex-1 min-w-0 text-sm font-semibold text-slate-800 truncate">{r.student_name}</span>
              {r.method && <span className="text-[11px] text-slate-500 bg-slate-100 rounded-md px-1.5 py-0.5">{METHOD_LABELS[r.method] || r.method}</span>}
              <span className="text-sm font-bold text-[#26A69A] w-20 text-right">+{fmtAmount(r.amount)} CHF</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
